import { supabase } from './supabase';
import { StorageLimitService } from './storage-limit-service';
import * as Sentry from '@sentry/react';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadResult {
  url: string;
  path: string;
  publicUrl: string;
  fileSize: number;
  mimeType: string;
  thumbnailUrl?: string;
  videoDuration?: number; // Duration in seconds for video files
}

export interface CreativeFile {
  id: string;
  name: string;
  type: 'image' | 'video';
  url: string;
  size: number;
  mimeType: string;
  thumbnailUrl?: string;
}

export class StorageService {
  private static readonly MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB
  private static readonly ALLOWED_IMAGE_TYPES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'
  ];
  private static readonly ALLOWED_VIDEO_TYPES = [
    'video/mp4', 'video/mpeg', 'video/mov', 'video/avi', 'video/x-flv', 'video/mpg', 
    'video/webm', 'video/wmv', 'video/3gpp', 'video/quicktime'
  ];

  /**
   * Upload a creative file to Supabase Storage
   */
  static async uploadCreative(
    file: File,
    projectId?: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    try {
      // Validate file
      this.validateFile(file);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Check storage limit before upload
      const storageCheck = await StorageLimitService.canUploadFile(user.id, file.size);
      
      if (!storageCheck.allowed) {
        console.error('❌ Storage limit exceeded:', storageCheck.reason);
        throw new Error(storageCheck.reason || 'Storage limit exceeded');
      }
      

      // Create file path: userId/projectId/filename (use 'unassigned' if no project)
      const fileExtension = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExtension}`;
      const folderName = projectId || 'unassigned';
      const filePath = `${user.id}/${folderName}/${fileName}`;

      // Simulate progress tracking with incremental updates
      if (onProgress) {
        onProgress({
          loaded: 0,
          total: file.size,
          percentage: 0
        });
      }

      // Simulate incremental progress for better UX
      let progressInterval: NodeJS.Timeout | null = null;
      let currentProgress = 0;
      
      if (onProgress) {
        progressInterval = setInterval(() => {
          if (currentProgress < 80) {
            currentProgress += Math.random() * 10 + 5; // Increase by 5-15% each time
            if (currentProgress > 80) currentProgress = 80;
            
            onProgress({
              loaded: Math.round((currentProgress / 100) * file.size),
              total: file.size,
              percentage: Math.round(currentProgress)
            });
          }
        }, 300); // Slower interval for more noticeable progress
      }

      try {
        // Upload file
        
        const { data, error } = await supabase.storage
          .from('creatives')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          console.error('❌ Supabase upload error:', error);
          throw new Error(`Upload failed: ${error.message}`);
        }
        

        // Complete progress
        if (progressInterval) {
          clearInterval(progressInterval);
        }
        
        if (onProgress) {
          onProgress({
            loaded: file.size,
            total: file.size,
            percentage: 100
          });
        }
      } catch (uploadError) {
        if (progressInterval) {
          clearInterval(progressInterval);
        }
        throw uploadError;
      }

      // Generate public URL for direct access (no expiration)
      const publicUrl = this.getPublicUrl(filePath, 'creatives');

      // Generate thumbnail and get duration for videos (non-blocking)
      let thumbnailUrl: string | undefined;
      let videoDuration: number | undefined;

      if (this.isVideoFile(file)) {
        try {
          const videoData = await this.generateVideoThumbnail(file, user.id, folderName, fileName);
          if (videoData) {
            thumbnailUrl = videoData.thumbnailUrl;
            videoDuration = videoData.duration;
          }
        } catch (thumbnailError) {
          // Thumbnail generation is optional - video can still be analyzed without it
          console.warn('⚠️ Thumbnail generation failed (non-blocking):', thumbnailError);
          console.warn('⚠️ Video will be uploaded without thumbnail - analysis can still proceed');
        }
      }

      // Record the file upload for storage tracking
      await StorageLimitService.recordFileUpload(user.id, file.size);

      return {
        url: publicUrl,
        path: filePath,
        publicUrl: publicUrl,
        fileSize: file.size,
        mimeType: file.type,
        thumbnailUrl,
        videoDuration
      };

    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  }

  /**
   * Upload multiple files in parallel
   */
  static async uploadMultipleCreatives(
    files: File[],
    projectId?: string,
    onProgress?: (fileIndex: number, progress: UploadProgress) => void
  ): Promise<UploadResult[]> {
    const uploadPromises = files.map((file, index) =>
      this.uploadCreative(file, projectId, (progress) => {
        if (onProgress) {
          onProgress(index, progress);
        }
      })
    );

    return Promise.all(uploadPromises);
  }

  /**
   * Delete a creative file from storage
   */
  static async deleteCreative(filePath: string): Promise<void> {
    try {
      const { error } = await supabase.storage
        .from('creatives')
        .remove([filePath]);

      if (error) {
        throw new Error(`Delete failed: ${error.message}`);
      }

      // Also delete thumbnail if it exists
      const thumbnailPath = this.getThumbnailPath(filePath);
      if (thumbnailPath) {
        await supabase.storage
          .from('thumbnails')
          .remove([thumbnailPath]);
      }

    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  }

  /**
   * Get public URL for direct access (no expiration)
   */
  static getPublicUrl(filePath: string, bucket: string = 'creatives'): string {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    return data.publicUrl;
  }

  /**
   * Get video streaming URL (now just returns the public URL)
   */
  static getVideoStreamingUrl(creativeUrl: string): string {
    // Since buckets are now public, we can return the URL directly
    return creativeUrl;
  }

  /**
   * List files for a user/project
   */
  static async listCreatives(projectId: string): Promise<CreativeFile[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const folderPath = `${user.id}/${projectId}`;
      
      const { data, error } = await supabase.storage
        .from('creatives')
        .list(folderPath, {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        throw new Error(`Failed to list files: ${error.message}`);
      }

      const creatives: CreativeFile[] = [];
      
      for (const file of data || []) {
        const filePath = `${folderPath}/${file.name}`;
        
        // Generate public URL for the creative file
        const publicUrl = this.getPublicUrl(filePath, 'creatives');

        const fileType = this.getFileType(file.metadata?.mimetype || '');
        let thumbnailUrl: string | undefined;

        if (fileType === 'video') {
          const thumbnailPath = this.getThumbnailPath(filePath);
          if (thumbnailPath) {
            thumbnailUrl = this.getPublicUrl(thumbnailPath, 'thumbnails');
          }
        }

        creatives.push({
          id: file.id || file.name,
          name: file.name,
          type: fileType,
          url: publicUrl,
          size: file.metadata?.size || 0,
          mimeType: file.metadata?.mimetype || '',
          thumbnailUrl
        });
      }

      return creatives;
    } catch (error) {
      console.error('List files error:', error);
      throw error;
    }
  }

  /**
   * Generate thumbnail for video files and extract video metadata
   */
  private static async generateVideoThumbnail(
    videoFile: File,
    userId: string,
    folderName: string,
    originalFileName: string
  ): Promise<{ thumbnailUrl?: string; duration?: number } | undefined> {
    try {
      
      // Create video element to capture frame
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Canvas context not available');
      }

      // Set video properties for faster loading
      video.muted = true;
      video.playsInline = true;
      video.style.display = 'none';

      // CRITICAL: Video must be in DOM for blob URLs to load in modern browsers
      document.body.appendChild(video);

      return new Promise((resolve, reject) => {
        let timeoutId: NodeJS.Timeout;
        let capturedDuration: number | undefined;

        // Set timeout for thumbnail generation (30 seconds max)
        timeoutId = setTimeout(() => {
          video.removeAttribute('src');
          URL.revokeObjectURL(video.src);
          if (video.parentNode) {
            document.body.removeChild(video);
          }
          reject(new Error('Thumbnail generation timeout'));
        }, 30000);

        const cleanup = () => {
          clearTimeout(timeoutId);
          if (video.src) {
            URL.revokeObjectURL(video.src);
          }
          // Remove video from DOM
          if (video.parentNode) {
            document.body.removeChild(video);
          }
        };

        video.addEventListener('loadedmetadata', async () => {
          try {
            
            // Capture the video duration
            capturedDuration = video.duration;
            
            // Set canvas dimensions to video dimensions (max 640x360 for thumbnails)
            const maxWidth = 640;
            const maxHeight = 360;
            
            let { videoWidth, videoHeight } = video;
            
            if (videoWidth > maxWidth || videoHeight > maxHeight) {
              const aspectRatio = videoWidth / videoHeight;
              if (videoWidth > videoHeight) {
                videoWidth = maxWidth;
                videoHeight = maxWidth / aspectRatio;
              } else {
                videoHeight = maxHeight;
                videoWidth = maxHeight * aspectRatio;
              }
            }

            canvas.width = videoWidth;
            canvas.height = videoHeight;
            

            // Seek to 10% of video duration for better thumbnail (but max 30 seconds)
            const seekTime = Math.min(video.duration * 0.1, 30);
            video.currentTime = seekTime;
          } catch (error) {
            cleanup();
            reject(error);
          }
        });

        video.addEventListener('seeked', async () => {
          try {
            
            // Draw video frame to canvas
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Convert canvas to blob with high quality
            canvas.toBlob(async (blob) => {
              if (!blob) {
                cleanup();
                reject(new Error('Failed to create thumbnail blob'));
                return;
              }

              try {
                
                // Create thumbnail filename based on original file
                const originalNameWithoutExt = originalFileName.split('.')[0];
                const thumbnailPath = `${userId}/${folderName}/thumb_${originalNameWithoutExt}.jpg`;
                
                
                // Ensure thumbnails bucket exists (verification only)
                await this.ensureThumbnailsBucketExists();
                
                const { data, error } = await supabase.storage
                  .from('thumbnails')
                  .upload(thumbnailPath, blob, {
                    cacheControl: '3600',
                    upsert: true // Allow overwriting existing thumbnails
                  });

                if (error) {
                  console.error('❌ Thumbnail upload error:', error);
                  console.error('❌ Error details:', {
                    message: error.message,
                    path: thumbnailPath
                  });
                  
                  // Check if it's a bucket not found error
                  if (error.message.includes('not found') || error.message.includes('does not exist')) {
                    console.error('❌ Thumbnails bucket does not exist. Video will not have a thumbnail.');
                    // Return undefined to indicate thumbnail generation failed
                    cleanup();
                    resolve({ duration: capturedDuration });
                    return;
                  }
                  
                  throw error;
                }


                // Generate public URL for thumbnail
                const thumbnailPublicUrl = this.getPublicUrl(thumbnailPath, 'thumbnails');

                cleanup();
                resolve({ 
                  thumbnailUrl: thumbnailPublicUrl,
                  duration: capturedDuration
                });
              } catch (error) {
                console.error('❌ Thumbnail upload failed:', error);
                cleanup();
                reject(error);
              }
            }, 'image/jpeg', 0.85); // High quality JPEG
          } catch (error) {
            cleanup();
            reject(error);
          }
        });

        video.addEventListener('error', () => {
          const errorDetails = video.error;
          const errorMessages = {
            1: 'MEDIA_ERR_ABORTED - Loading was aborted',
            2: 'MEDIA_ERR_NETWORK - Network error occurred',
            3: 'MEDIA_ERR_DECODE - Video is corrupted or unsupported format',
            4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - Video format not supported or source blocked'
          };

          const errorCode = errorDetails?.code || 0;
          const errorMessage = errorMessages[errorCode as keyof typeof errorMessages] || 'Unknown error';

          console.error('❌ Video loading error details:', {
            code: errorCode,
            message: errorMessage,
            videoSrc: video.src.substring(0, 100),
            videoType: videoFile.type,
            videoSize: videoFile.size
          });

          cleanup();
          reject(new Error(`Video loading failed: ${errorMessage}`));
        });

        // Start loading the video
        video.src = URL.createObjectURL(videoFile);
        video.load();
      });
    } catch (error) {
      console.error('❌ Thumbnail generation error:', error);
      return undefined;
    }
  }

  /**
   * Ensure thumbnails bucket exists
   */
  private static async ensureThumbnailsBucketExists(): Promise<void> {
    try {
      // Simply try to list files in the thumbnails bucket to verify it exists
      // This is a much lighter operation than listing all buckets
      const { error: testError } = await supabase.storage
        .from('thumbnails')
        .list('', { limit: 1 });
      
      if (testError) {
        // If we get a "Bucket not found" error, the bucket doesn't exist
        if (testError.message.includes('not found') || testError.message.includes('does not exist')) {
          // Don't throw error - we'll attempt upload anyway and let it fail gracefully
          return;
        } else {
          // Other errors (like permission issues) should not prevent us from trying
          console.warn('⚠️ Could not verify thumbnails bucket:', testError.message);
          return;
        }
      }
      
    } catch (error) {
      console.warn('⚠️ Error verifying thumbnails bucket exists:', error);
      // Don't throw error, just log it - we can still try to upload
    }
  }

  /**
   * Validate file before upload
   */
  private static validateFile(file: File): void {
    // Check file type first
    const isImage = this.ALLOWED_IMAGE_TYPES.includes(file.type);
    const isVideo = this.ALLOWED_VIDEO_TYPES.includes(file.type);

    if (!isImage && !isVideo) {
      throw new Error('File type not supported. Please upload images (JPEG, PNG, GIF, WebP, HEIC, HEIF) or videos (MP4, MPEG, MOV, AVI, FLV, MPG, WebM, WMV, 3GPP)');
    }

    // Check file size based on type (matching Gemini limits)
    const maxImageSize = 20 * 1024 * 1024; // 20MB for images
    const maxVideoSize = 1024 * 1024 * 1024; // 1GB for videos
    
    if (isImage && file.size > maxImageSize) {
      throw new Error(`Image file size exceeds maximum limit of ${maxImageSize / (1024 * 1024)}MB`);
    }
    
    if (isVideo && file.size > maxVideoSize) {
      throw new Error(`Video file size exceeds maximum limit of ${maxVideoSize / (1024 * 1024)}MB`);
    }
  }

  /**
   * Get file type based on MIME type
   */
  private static getFileType(mimeType: string): 'image' | 'video' {
    if (this.ALLOWED_IMAGE_TYPES.includes(mimeType)) {
      return 'image';
    }
    return 'video';
  }

  /**
   * Check if file is a video
   */
  private static isVideoFile(file: File): boolean {
    return this.ALLOWED_VIDEO_TYPES.includes(file.type);
  }

  /**
   * Get thumbnail path for a given file path
   */
  private static getThumbnailPath(filePath: string): string | undefined {
    const pathParts = filePath.split('/');
    if (pathParts.length < 3) return undefined;
    
    const fileName = pathParts[pathParts.length - 1];
    const thumbnailName = `thumb_${fileName.split('.')[0]}.jpg`;
    
    return `${pathParts[0]}/${pathParts[1]}/${thumbnailName}`;
  }

  /**
   * Get file info without downloading
   */
  static async getFileInfo(filePath: string): Promise<{
    size: number;
    mimeType: string;
    lastModified: string;
  }> {
    try {
      const { data, error } = await supabase.storage
        .from('creatives')
        .list(filePath.split('/').slice(0, -1).join('/'), {
          search: filePath.split('/').pop()
        });

      if (error) {
        throw error;
      }

      const fileInfo = data?.[0];
      if (!fileInfo) {
        throw new Error('File not found');
      }

      return {
        size: fileInfo.metadata?.size || 0,
        mimeType: fileInfo.metadata?.mimetype || '',
        lastModified: fileInfo.updated_at || fileInfo.created_at || ''
      };
    } catch (error) {
      console.error('Get file info error:', error);
      throw error;
    }
  }

  /**
   * Upload enhanced image to storage
   */
  static async uploadEnhancedImage(
    base64Data: string,
    originalFileName: string,
    analysisId: string
  ): Promise<{ url?: string; error?: string }> {
    try {
      // Convert base64 to blob
      const base64WithoutPrefix = base64Data.replace(/^data:image\/\w+;base64,/, '');
      const byteCharacters = atob(base64WithoutPrefix);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { error: 'User not authenticated' };
      }

      // Generate unique filename
      const timestamp = Date.now();
      const fileName = `enhanced_${analysisId}_${timestamp}.png`;
      const filePath = `${user.id}/enhanced/${fileName}`;

      // Upload to storage
      const { data, error } = await supabase.storage
        .from('creatives')
        .upload(filePath, blob, {
          contentType: 'image/png',
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Error uploading enhanced image:', error);
        return { error: error.message };
      }

      // Generate public URL for direct access
      const publicUrl = this.getPublicUrl(filePath, 'creatives');

      return { url: publicUrl };
    } catch (error) {
      console.error('Error in uploadEnhancedImage:', error);
      return { error: 'Failed to upload enhanced image' };
    }
  }

  /**
   * Upload generated ad image to storage
   */
  static async uploadGeneratedAd(
    base64Data: string,
    generationId: string
  ): Promise<{ url?: string; thumbnailUrl?: string; error?: string }> {
    try {
      // Convert base64 to blob
      const base64WithoutPrefix = base64Data.replace(/^data:image\/\w+;base64,/, '');
      const byteCharacters = atob(base64WithoutPrefix);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { error: 'User not authenticated' };
      }

      // Generate unique filename
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 8);
      const fileName = `gen_${generationId}_${timestamp}_${randomString}.png`;
      const filePath = `${user.id}/ad-generations/${fileName}`;


      // Upload to storage
      const { data, error } = await supabase.storage
        .from('creatives')
        .upload(filePath, blob, {
          contentType: 'image/png',
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('❌ Error uploading generated ad:', error);
        return { error: error.message };
      }

      // Generate public URL for direct access
      const publicUrl = this.getPublicUrl(filePath, 'creatives');


      // Generate thumbnail in background
      let thumbnailUrl: string | undefined;
      try {
        const { ThumbnailService } = await import('./thumbnail-service');
        thumbnailUrl = await ThumbnailService.createAndUploadThumbnailFromUrl(
          publicUrl,
          `gen_${generationId}`,
          { width: 600, height: 600, quality: 100 }
        );
      } catch (thumbnailError) {
        console.warn('⚠️ Failed to generate thumbnail (non-blocking):', thumbnailError);
        // Don't fail the whole operation if thumbnail generation fails
      }

      return { 
        url: publicUrl,
        thumbnailUrl
      };

    } catch (error) {
      console.error('❌ Error in uploadGeneratedAd:', error);
      return { error: 'Failed to upload generated ad' };
    }
  }
}

export default StorageService; 